import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  CreditCard,
  Plus,
  Settings,
  BarChart3,
  Clock,
  Shield,
  Copy,
  Eye,
  Trash2,
  RefreshCw,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  AlertCircle
} from 'lucide-react';
import Footer from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgencyPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [agency, setAgency] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('profiles');
  
  // Form states
  const [showCreateAgency, setShowCreateAgency] = useState(false);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [newProfile, setNewProfile] = useState({
    client_name: '',
    client_email: '',
    credit_baseline: 0
  });
  
  const token = localStorage.getItem('session_token');

  const fetchAgencyStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/agency/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      setHasAccess(data.has_access);
      if (data.has_agency && data.agency) {
        setAgency(data.agency);
      }
    } catch (error) {
      console.error('Error fetching agency status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchProfiles = useCallback(async () => {
    if (!agency) return;
    try {
      const response = await fetch(`${API_URL}/api/agency/profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    }
  }, [agency, token]);

  const fetchAnalytics = useCallback(async () => {
    if (!agency) return;
    try {
      const response = await fetch(`${API_URL}/api/agency/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [agency, token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAgencyStatus();
  }, [token, navigate, fetchAgencyStatus]);

  useEffect(() => {
    if (agency) {
      fetchProfiles();
      fetchAnalytics();
    }
  }, [agency, fetchProfiles, fetchAnalytics]);

  const handleCreateAgency = async () => {
    if (!agencyName.trim()) {
      toast.error('Please enter an agency name');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/agency/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ agency_name: agencyName })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Agency created successfully!');
        setShowCreateAgency(false);
        fetchAgencyStatus();
      } else {
        toast.error(data.detail || 'Failed to create agency');
      }
    } catch (error) {
      toast.error('Error creating agency');
    }
  };

  const handleCreateProfile = async () => {
    if (!newProfile.client_name.trim()) {
      toast.error('Please enter client name');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/agency/profiles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProfile)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Profile created! Access code: ${data.access_code}`);
        setShowCreateProfile(false);
        setNewProfile({ client_name: '', client_email: '', credit_baseline: 0 });
        fetchProfiles();
      } else {
        toast.error(data.detail || 'Failed to create profile');
      }
    } catch (error) {
      toast.error('Error creating profile');
    }
  };

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied to clipboard');
  };

  const deleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to deactivate this profile?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/agency/profiles/${profileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Profile deactivated');
        fetchProfiles();
      } else {
        toast.error('Failed to deactivate profile');
      }
    } catch (error) {
      toast.error('Error deactivating profile');
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      twitter: <Twitter className="w-4 h-4" />,
      facebook: <Facebook className="w-4 h-4" />,
      instagram: <Instagram className="w-4 h-4" />,
      linkedin: <Linkedin className="w-4 h-4" />
    };
    return icons[platform] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <div className="max-w-4xl mx-auto p-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
              <CardTitle className="text-2xl text-white">Agency Portal</CardTitle>
              <CardDescription className="text-gray-400">
                Manage multiple client social media accounts from one dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-gray-400">
                The Agency Portal is available exclusively for <strong className="text-purple-400">Supremium</strong> subscribers.
              </p>
              <div className="bg-gray-800/50 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-white mb-2">Features include:</h3>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" /> Unlimited client profiles
                  </li>
                  <li className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-purple-400" /> Per-profile credit tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-purple-400" /> Read-only client portal
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-400" /> Anti-throttling cross-posting
                  </li>
                </ul>
              </div>
            </CardContent>
            <CardFooter className="justify-center">
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Upgrade to Supremium
              </Button>
            </CardFooter>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <div className="max-w-4xl mx-auto p-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-purple-400" />
              </div>
              <CardTitle className="text-2xl text-white">Create Your Agency</CardTitle>
              <CardDescription className="text-gray-400">
                Set up your social media management agency to start managing client accounts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {showCreateAgency ? (
                <div className="space-y-4">
                  <Input
                    placeholder="Agency Name (e.g., Acme Social Media)"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleCreateAgency}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Create Agency
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => setShowCreateAgency(false)}
                      className="border-gray-700 text-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowCreateAgency(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" /> Get Started
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Building2 className="w-8 h-8 text-purple-400" />
              {agency.agency_name}
            </h1>
            <p className="text-gray-400 mt-1">Agency Portal</p>
          </div>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            Supremium
          </Badge>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{profiles.length}</p>
                  <p className="text-sm text-gray-400">Client Profiles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {analytics?.credits?.total_allocated || 0}
                  </p>
                  <p className="text-sm text-gray-400">Credits Allocated</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {analytics?.credits?.total_used || 0}
                  </p>
                  <p className="text-sm text-gray-400">Credits Used</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {analytics?.content?.total_posts || 0}
                  </p>
                  <p className="text-sm text-gray-400">Posts Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="profiles" className="data-[state=active]:bg-purple-600">
              <Users className="w-4 h-4 mr-2" /> Client Profiles
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-600">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-600">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Profiles Tab */}
          <TabsContent value="profiles" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Client Profiles</h2>
              <Button 
                onClick={() => setShowCreateProfile(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </div>

            {/* Create Profile Form */}
            {showCreateProfile && (
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">New Client Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Client Name *"
                      value={newProfile.client_name}
                      onChange={(e) => setNewProfile({ ...newProfile, client_name: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Input
                      type="email"
                      placeholder="Client Email (optional)"
                      value={newProfile.client_email}
                      onChange={(e) => setNewProfile({ ...newProfile, client_email: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Monthly Credit Baseline</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={newProfile.credit_baseline}
                      onChange={(e) => setNewProfile({ ...newProfile, credit_baseline: parseInt(e.target.value) || 0 })}
                      className="bg-gray-800 border-gray-700 text-white w-48"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleCreateProfile} className="bg-purple-600 hover:bg-purple-700">
                      Create Profile
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCreateProfile(false)}
                      className="border-gray-700 text-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Profiles List */}
            {profiles.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No client profiles yet. Add your first client to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {profiles.map((profile) => (
                  <Card key={profile.profile_id} className="bg-gray-900 border-gray-800">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg text-white">{profile.client_name}</CardTitle>
                          <CardDescription className="text-gray-400">
                            {profile.client_email || 'No email'}
                          </CardDescription>
                        </div>
                        <Badge 
                          className={profile.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-500/20 text-gray-400'
                          }
                        >
                          {profile.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Credit Baseline</p>
                          <p className="text-white font-medium">{profile.credit_baseline}/mo</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Used This Month</p>
                          <p className="text-white font-medium">{profile.credits_used_this_month || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Posts</p>
                          <p className="text-white font-medium">{profile.posts_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Platforms</p>
                          <div className="flex gap-1">
                            {profile.connected_platforms?.length > 0 ? (
                              profile.connected_platforms.map((conn, i) => (
                                <span key={i}>{getPlatformIcon(conn.platform)}</span>
                              ))
                            ) : (
                              <span className="text-gray-500">None</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {profile.access_code && (
                        <div className="bg-gray-800/50 rounded p-2 flex items-center justify-between">
                          <span className="text-xs text-gray-400">
                            Access Code: <code className="text-purple-400">{profile.access_code}</code>
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyAccessCode(profile.access_code)}
                            className="h-6 w-6 p-0"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="pt-0">
                      <div className="flex gap-2 w-full">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/agency/profile/${profile.profile_id}`)}
                          className="flex-1 border-gray-700 text-gray-300"
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => deleteProfile(profile.profile_id)}
                          className="border-red-800 text-red-400 hover:bg-red-950"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Agency Analytics</CardTitle>
                <CardDescription className="text-gray-400">
                  Overview of your agency's performance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analytics ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h4 className="text-sm text-gray-400 mb-1">Total Profiles</h4>
                        <p className="text-2xl font-bold text-white">{analytics.profiles?.total || 0}</p>
                        <p className="text-xs text-green-400">{analytics.profiles?.active || 0} active</p>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h4 className="text-sm text-gray-400 mb-1">Credit Utilization</h4>
                        <p className="text-2xl font-bold text-white">{analytics.credits?.utilization || '0%'}</p>
                        <p className="text-xs text-gray-400">
                          {analytics.credits?.total_used || 0} / {analytics.credits?.total_allocated || 0} used
                        </p>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h4 className="text-sm text-gray-400 mb-1">Total Content</h4>
                        <p className="text-2xl font-bold text-white">{analytics.content?.total_posts || 0}</p>
                        <p className="text-xs text-gray-400">posts created</p>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm text-gray-400 mb-2">Recent Activity</h4>
                      {analytics.recent_activity?.length > 0 ? (
                        <div className="space-y-2">
                          {analytics.recent_activity.map((activity, i) => (
                            <div key={i} className="bg-gray-800/50 rounded p-2 text-sm">
                              <span className="text-gray-300">{activity.content?.substring(0, 50)}...</span>
                              <span className="text-gray-500 ml-2">
                                {new Date(activity.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">No recent activity</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">Loading analytics...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Agency Settings</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage your agency configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Agency Name</label>
                  <Input
                    value={agency.agency_name}
                    disabled
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Agency ID</label>
                  <Input
                    value={agency.agency_id}
                    disabled
                    className="bg-gray-800 border-gray-700 text-gray-500 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Created</label>
                  <Input
                    value={new Date(agency.created_at).toLocaleString()}
                    disabled
                    className="bg-gray-800 border-gray-700 text-gray-500"
                  />
                </div>
                
                <div className="pt-4 border-t border-gray-800">
                  <div className="flex items-start gap-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-400">Anti-Throttling Enabled</h4>
                      <p className="text-sm text-gray-400">
                        All cross-platform posts are automatically staggered to avoid detection. 
                        Posts are always human-approved before publishing.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default AgencyPortal;
